/**
 * Episode Management API Types
 *
 * Types for the episode management endpoints under the Drama/Series aggregate.
 */

import type { VideoVariant } from './movie';

export type EpisodeStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING';

/**
 * Episode summary row – matches EpisodeResponse from backend.
 */
export interface Episode {
  episodeId: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  description: string;
  /** Total seconds. */
  duration: number;
  thumbnailUrl?: string | null;
  status: EpisodeStatus;
  views: number;
  releaseDate: string;
  uploadDate: string;
}

/**
 * Preview image for episode detail.
 * Shape is simplified; backend may expose additional metadata.
 */
export interface EpisodePreviewImage {
  imageId?: string;
  imageUrl: string;
  sortOrder?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Episode detail response with media.
 */
export interface EpisodeDetailResponse {
  episode: Episode;
  videoVariants: VideoVariant[];
  previewImages: EpisodePreviewImage[];
}

/**
 * Paginated episodes list.
 */
export interface EpisodesPage {
  items: Episode[];
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Query params for listing episodes.
 */
export interface EpisodesQueryParams {
  page?: number;
  size?: number;
  season?: number;
  keyword?: string;
  status?: EpisodeStatus | string;
}

/**
 * Create Episode Request payload.
 *
 * Used as JSON body in both multipart (`data` field) and JSON-only endpoints.
 */
export interface EpisodeCreateRequest {
  // Some backends require dramaId in the body in addition to the path variable.
  dramaId?: string;
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  description: string;
  releaseDate: string;
  /** Total seconds. */
  duration: number;
}

/**
 * Update Episode Request payload.
 */
export interface EpisodeUpdateRequest {
  seasonNumber?: number;
  episodeNumber?: number;
  title?: string;
  description?: string;
  releaseDate?: string;
  /** Total seconds. */
  duration?: number;
  thumbnailUrl?: string | null;
  status?: EpisodeStatus;
}

/**
 * Helper for SxEy display label.
 */
export const formatSeasonEpisodeLabel = (
  seasonNumber: number | null | undefined,
  episodeNumber: number | null | undefined
): string => {
  if (seasonNumber == null || episodeNumber == null) return '-';
  return `S${seasonNumber}E${episodeNumber}`;
};


